import React, { useState, useEffect } from 'react';
import { Users, UserPlus, X, Mail, Check, Loader2, Crown, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../../utils/formatError';

const Team = () => {
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [role, setRole] = useState('owner');
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeam(res.data.team);
      setRole(res.data.role);
    } catch (e) {
      console.error('Failed to fetch team', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchTeam(); }, []);

  const createTeam = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/team/create`, {
        business_name: user?.business_name || 'My Team',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTeam(res.data.team);
      toast.success('Team created');
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to create team');
    }
    setCreating(false);
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/team/invite`, {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setInviteEmail('');
      setInviteName('');
      setShowInviteForm(false);
      fetchTeam();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to invite');
    }
    setInviting(false);
  };

  const removeMember = async (email) => {
    if (!confirm(`Remove ${email} from the team?`)) return;
    try {
      await axios.delete(`${API}/team/member/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Member removed');
      fetchTeam();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-black/40">Loading...</div>
      </div>
    );
  }

  const members = team?.members || [];
  const pending = team?.pending_invites || [];
  const seatCount = members.length + 1; // +1 for owner
  const additionalSeats = Math.max(0, seatCount - 1);
  const monthlyCost = additionalSeats * 25;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl headline text-black mb-2">Team</h1>
        <p className="text-black/60 body-text">
          Manage your Springboard team seats. Each additional seat is $25/month.
        </p>
      </div>

      {!team ? (
        /* No team yet — create one */
        <div className="bg-white rounded-none border border-border p-8 text-center" data-testid="team-create-section">
          <Building2 className="h-10 w-10 mx-auto mb-4" style={{ color: '#d97706' }} />
          <h2 className="text-xl font-bold text-black mb-2">Create Your Team</h2>
          <p className="text-black/60 text-sm mb-6 max-w-sm mx-auto">
            Set up a team to share your Springboard workspace with colleagues and partners.
          </p>
          <Button
            onClick={createTeam}
            disabled={creating}
            className="rounded-none"
            style={{ background: '#d97706', color: '#fff' }}
            data-testid="team-create-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
            Create Team
          </Button>
        </div>
      ) : (
        <>
          {/* Seat Overview */}
          <div className="bg-white rounded-none border border-border p-6" data-testid="team-overview">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-black">{team.business_name}</h2>
                <p className="text-black/50 text-sm">
                  {seatCount} seat{seatCount !== 1 ? 's' : ''} active
                  {monthlyCost > 0 && ` — $${monthlyCost}/mo for additional seats`}
                </p>
              </div>
              <Button
                onClick={() => setShowInviteForm(true)}
                className="rounded-none"
                style={{ background: '#d97706', color: '#fff' }}
                data-testid="team-invite-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Seat
              </Button>
            </div>

            {/* Seat usage bar */}
            <div className="mb-2">
              <div className="flex justify-between text-xs text-black/50 mb-1">
                <span>Seats Used</span>
                <span>{seatCount} / {team.max_seats || seatCount}</span>
              </div>
              <div className="h-2 bg-[#f0f0f0] rounded-none overflow-hidden">
                <div
                  className="h-full rounded-none transition-all"
                  style={{
                    width: `${Math.min(100, (seatCount / Math.max(team.max_seats || seatCount, 1)) * 100)}%`,
                    background: '#d97706',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Invite Form */}
          {showInviteForm && (
            <div className="bg-white rounded-none border border-border p-6" data-testid="team-invite-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-black">Add a team member</h3>
                <button onClick={() => setShowInviteForm(false)} className="text-black/40 hover:text-black">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <form onSubmit={inviteMember} className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-black/60 block mb-1">Name</label>
                  <Input
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Team member's name"
                    className="rounded-none"
                    data-testid="team-invite-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-black/60 block mb-1">Email</label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@business.com"
                    required
                    className="rounded-none"
                    data-testid="team-invite-email"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={inviting} className="rounded-none" style={{ background: '#d97706', color: '#fff' }}
                    data-testid="team-invite-submit">
                    {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Add Member — +$25/mo
                  </Button>
                  <Button type="button" variant="ghost" className="rounded-none" onClick={() => setShowInviteForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Members List */}
          <div className="bg-white rounded-none border border-border overflow-hidden" data-testid="team-members-list">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-black text-sm">Team Members</h3>
            </div>

            {/* Owner */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-[rgba(255,192,0,0.15)] flex items-center justify-center">
                  <Crown className="h-5 w-5" style={{ color: '#d97706' }} />
                </div>
                <div>
                  <p className="font-semibold text-black text-sm">{team.owner_name}</p>
                  <p className="text-black/50 text-xs">{team.owner_email}</p>
                </div>
              </div>
              <span className="text-xs font-semibold px-3 py-1 rounded-none" style={{ background: 'rgba(255,192,0,0.15)', color: '#1E491A' }}>
                Owner
              </span>
            </div>

            {/* Active Members */}
            {members.map((m, i) => (
              <div key={m.email} className="flex items-center justify-between p-4 border-b border-border/50" data-testid={`team-member-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-none bg-[#f0f0f0] flex items-center justify-center">
                    <span className="text-sm font-bold text-black/60">
                      {(m.name || m.email)[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-black text-sm">{m.name || m.email}</p>
                    <p className="text-black/50 text-xs">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-1 rounded-none bg-[rgba(255,192,0,0.1)] text-[#1E491A] flex items-center gap-1">
                    <Check className="h-3 w-3" /> Active
                  </span>
                  {role === 'owner' && (
                    <button
                      onClick={() => removeMember(m.email)}
                      className="text-black/30 hover:text-red-500 transition-colors"
                      data-testid={`team-remove-${i}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Pending Invites */}
            {pending.map((p, i) => (
              <div key={p.email} className="flex items-center justify-between p-4 border-b border-border/50 opacity-70" data-testid={`team-pending-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-none bg-[#f8f8f8] flex items-center justify-center">
                    <Mail className="h-4 w-4 text-black/30" />
                  </div>
                  <div>
                    <p className="font-semibold text-black text-sm">{p.name || p.email}</p>
                    <p className="text-black/50 text-xs">{p.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-1 rounded-none bg-amber-50 text-amber-700">
                    Pending
                  </span>
                  {role === 'owner' && (
                    <button
                      onClick={() => removeMember(p.email)}
                      className="text-black/30 hover:text-red-500 transition-colors"
                      data-testid={`team-cancel-invite-${i}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {members.length === 0 && pending.length === 0 && (
              <div className="p-8 text-center text-black/40 text-sm">
                No team members yet. Click "Add Seat" to invite someone.
              </div>
            )}
          </div>

          {/* Pricing info */}
          <div className="bg-[rgba(255,192,0,0.15)] rounded-none p-5 text-center" data-testid="team-pricing-info">
            <p className="text-sm text-[#1E491A] font-medium">
              Owner seat included. Each additional seat is <strong>$25/month</strong>.
              {monthlyCost > 0 && (
                <> Your current additional cost: <strong>${monthlyCost}/mo</strong>.</>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Team;
